import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Video, Clock, CheckCircle, AlertTriangle, XCircle, ArrowLeft, Bell } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import Footer from '../components/Footer';
import { authFetch } from '../lib/authFetch';
import useTheme4A from '../components/useTheme4A';
import useAllowPrint from '../components/useAllowPrint';

// Check-in interval in minutes
const CHECK_IN_INTERVAL = 10;
// Minimum check-ins required for verified attendance (for a 60 min meeting)
const MIN_CHECKINS_REQUIRED = 4;
// Grace period to respond to check-in (in seconds)
const CHECKIN_GRACE_PERIOD = 120;

function MeetingVerificationPage() {
  useAllowPrint();
  useTheme4A();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get meeting info from URL params
  const meetingId = searchParams.get('id') || '';
  const meetingName = searchParams.get('name') || 'Recovery Meeting';
  const meetingType = searchParams.get('type') || 'AA';
  const zoomLink = searchParams.get('zoom') || '';
  
  const [sessionId, setSessionId] = useState('');
  const [startTime, setStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [checkIns, setCheckIns] = useState([]);
  const [showCheckIn, setShowCheckIn] = useState(false);
  const [checkInCountdown, setCheckInCountdown] = useState(CHECKIN_GRACE_PERIOD);
  const [missedCheckIns, setMissedCheckIns] = useState(0);
  const [isVerified, setIsVerified] = useState(false);
  const [sessionEnded, setSessionEnded] = useState(false);
  const [nextCheckInTime, setNextCheckInTime] = useState(null);

  const triggerCheckIn = useCallback(() => {
    setShowCheckIn(true);
    setCheckInCountdown(CHECKIN_GRACE_PERIOD);

    // Send browser notification
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('Meeting Check-In Required', {
        body: `Please confirm you're still attending ${meetingName}`,
        icon: '/favicon.ico',
        requireInteraction: true
      });
    }

    // Play a sound
    try {
      const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleRkjmObpvnUhS6zh8LJ3J16k2+amdRVQo9zmn3EOS5zc4ppwB0qc3N+YcAA=');
      audio.volume = 0.5;
      audio.play();
    } catch (e) {
      console.debug('[Verification] check-in sound failed:', e?.message);
    }
  }, [meetingName]);

  const handleMissedCheckIn = useCallback(async () => {
    setMissedCheckIns(prev => prev + 1);
    setShowCheckIn(false);

    // Schedule next check-in anyway
    const next = new Date(Date.now() + CHECK_IN_INTERVAL * 60 * 1000);
    setNextCheckInTime(next);

    toast.error('Check-in missed! This may affect your attendance verification.');

    // Log to backend
    try {
      await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/meetings/verification/missed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          missed_at: new Date().toISOString()
        })
      });
    } catch (error) {
      console.error('Error logging missed check-in:', error);
    }
  }, [sessionId]);

  // Initialize session
  useEffect(() => {
    const initSession = async () => {
      const newSessionId = `verify_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      setSessionId(newSessionId);
      setStartTime(new Date());
      
      // Schedule first check-in
      const firstCheckIn = new Date(Date.now() + CHECK_IN_INTERVAL * 60 * 1000);
      setNextCheckInTime(firstCheckIn);
      
      // Log session start to backend
      try {
        await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/meetings/verification/start`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
            session_id: newSessionId,
            meeting_id: meetingId,
            meeting_name: meetingName,
            meeting_type: meetingType,
            zoom_link: zoomLink
          })
        });
      } catch (error) {
        console.error('Error starting verification session:', error);
      }
      
      // Request notification permission
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission();
      }
    };
    
    initSession();
    
    return () => {
      // Cleanup on unmount
    };
  }, [meetingId, meetingName, meetingType, zoomLink]);

  // Elapsed time counter
  useEffect(() => {
    if (!startTime || sessionEnded) return;
    
    const timer = setInterval(() => {
      const now = new Date();
      const elapsed = Math.floor((now - startTime) / 1000);
      setElapsedTime(elapsed);
    }, 1000);
    
    return () => clearInterval(timer);
  }, [startTime, sessionEnded]);

  // The check-in scheduler depends on triggerCheckIn (memoised below).  Including
  // it in deps is correct — triggerCheckIn is stable as long as meetingName doesn't
  // change, so we don't get spurious re-runs.
  useEffect(() => {
    if (!startTime || sessionEnded) return;
    
    const checkSchedule = setInterval(() => {
      const now = new Date();
      if (nextCheckInTime && now >= nextCheckInTime && !showCheckIn) {
        triggerCheckIn();
      }
    }, 1000);
    
    return () => clearInterval(checkSchedule);
  }, [startTime, nextCheckInTime, showCheckIn, sessionEnded, triggerCheckIn]);

  // Check-in countdown timer
  useEffect(() => {
    if (!showCheckIn) return;
    
    const countdown = setInterval(() => {
      setCheckInCountdown(prev => {
        if (prev <= 1) {
          // Missed check-in
          handleMissedCheckIn();
          return CHECKIN_GRACE_PERIOD;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(countdown);
  }, [showCheckIn, handleMissedCheckIn]);

  const handleCheckIn = async () => {
    const checkInTime = new Date();
    const newCheckIn = {
      time: checkInTime.toISOString(),
      elapsed_minutes: Math.floor(elapsedTime / 60)
    };
    
    setCheckIns(prev => [...prev, newCheckIn]);
    setShowCheckIn(false);
    setCheckInCountdown(CHECKIN_GRACE_PERIOD);
    
    // Schedule next check-in
    const next = new Date(Date.now() + CHECK_IN_INTERVAL * 60 * 1000);
    setNextCheckInTime(next);
    
    toast.success('Check-in recorded! Keep attending the meeting.');
    
    // Log to backend
    try {
      await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/meetings/verification/checkin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
          session_id: sessionId,
          checkin_time: checkInTime.toISOString(),
          elapsed_minutes: Math.floor(elapsedTime / 60)
        })
      });
    } catch (error) {
      console.error('Error logging check-in:', error);
    }
    
    // Check if verified
    if (checkIns.length + 1 >= MIN_CHECKINS_REQUIRED) {
      setIsVerified(true);
    }
  };

  const endSession = async () => {
    setSessionEnded(true);
    
    const verified = checkIns.length >= MIN_CHECKINS_REQUIRED && missedCheckIns < 2;
    
    // Log session end to backend
    try {
      await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/meetings/verification/end`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
          session_id: sessionId,
          total_checkins: checkIns.length,
          missed_checkins: missedCheckIns,
          duration_minutes: Math.floor(elapsedTime / 60),
          verified: verified
        })
      });
      
      if (verified) {
        toast.success('Meeting attendance verified! Great job staying committed.');
      } else {
        toast.warning('Meeting ended. Attendance could not be fully verified.');
      }
    } catch (error) {
      console.error('Error ending session:', error);
    }
    
    // Navigate back after delay
    setTimeout(() => {
      navigate('/meetings');
    }, 3000);
  };

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hrs > 0) {
      return `${hrs}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    }
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  const getTimeUntilNextCheckIn = () => {
    if (!nextCheckInTime) return null;
    const now = new Date();
    const diff = Math.max(0, Math.floor((nextCheckInTime - now) / 1000));
    return formatTime(diff);
  };

  const progress = Math.min(100, (checkIns.length / MIN_CHECKINS_REQUIRED) * 100);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 pb-20">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/10 backdrop-blur-md border-b border-white/20 z-40 px-4 py-3">
        <div className="flex items-center justify-between">
          <button 
            onClick={() => navigate(-1)} 
            className="text-white/80 hover:text-white flex items-center gap-2"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm">Exit</span>
          </button>
          <div className="text-center">
            <p className="text-white font-semibold text-sm">{meetingName}</p>
            <p className="text-white/60 text-xs">{meetingType} Meeting</p>
          </div>
          <div className="w-16"></div>
        </div>
      </header>

      <main className="pt-20 px-4">
        <div className="max-w-md mx-auto">
          {/* Check-in Modal */}
          {showCheckIn && !sessionEnded && (
            <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl p-6 max-w-sm w-full animate-pulse-slow">
                <div className="text-center">
                  <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Bell className="w-8 h-8 text-amber-600 animate-bounce" />
                  </div>
                  <h2 className="text-xl font-bold text-zinc-900 mb-2">Still Here?</h2>
                  <p className="text-zinc-600 text-sm mb-4">
                    Confirm you're still attending the meeting to verify your attendance.
                  </p>
                  <div className="text-3xl font-mono font-bold text-amber-600 mb-4">
                    {Math.floor(checkInCountdown / 60)}:{String(checkInCountdown % 60).padStart(2, '0')}
                  </div>
                  <Button 
                    onClick={handleCheckIn}
                    className="w-full bg-green-600 hover:bg-green-700 text-lg py-6"
                    data-testid="checkin-button"
                  >
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Yes, I'm Here!
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Session Ended */}
          {sessionEnded && (
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-6 mb-6 text-center">
              <div className={`w-16 h-16 ${isVerified ? 'bg-green-500' : 'bg-amber-500'} rounded-full flex items-center justify-center mx-auto mb-4`}>
                {isVerified ? (
                  <CheckCircle className="w-8 h-8 text-white" />
                ) : (
                  <AlertTriangle className="w-8 h-8 text-white" />
                )}
              </div>
              <h2 className="text-xl font-bold text-white mb-2">
                {isVerified ? 'Attendance Verified!' : 'Session Ended'}
              </h2>
              <p className="text-white/80 text-sm mb-4">
                {isVerified 
                  ? 'Great job! Your meeting attendance has been verified and logged.'
                  : 'Your attendance could not be fully verified due to missed check-ins.'
                }
              </p>
              <p className="text-white/60 text-xs">Redirecting to meetings...</p>
            </div>
          )}

          {/* Meeting Timer */}
          {!sessionEnded && (
            <>
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-6 mb-6 text-center">
                <Video className="w-10 h-10 text-white mx-auto mb-3" />
                <p className="text-white/60 text-xs uppercase tracking-wider mb-2">Meeting Duration</p>
                <p className="text-4xl font-mono font-bold text-white mb-4" data-testid="elapsed-time">
                  {formatTime(elapsedTime)}
                </p>
                
                {/* Progress to verification */}
                <div className="mb-4">
                  <div className="flex justify-between text-xs text-white/60 mb-1">
                    <span>Verification Progress</span>
                    <span>{checkIns.length}/{MIN_CHECKINS_REQUIRED} check-ins</span>
                  </div>
                  <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                    <div 
                      className={`h-full transition-all duration-500 ${isVerified ? 'bg-green-400' : 'bg-amber-400'}`}
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
                
                {isVerified && (
                  <div className="flex items-center justify-center gap-2 text-green-400 text-sm">
                    <CheckCircle className="w-4 h-4" />
                    <span>Attendance Verified</span>
                  </div>
                )}
              </div>

              {/* Next Check-in */}
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-white/60" />
                    <div>
                      <p className="text-white/60 text-xs">Next Check-in</p>
                      <p className="text-white font-mono text-lg" data-testid="next-checkin">
                        {getTimeUntilNextCheckIn() || 'Soon'}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-white/60 text-xs">Check-ins</p>
                    <p className="text-white font-bold">{checkIns.length}</p>
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-lg p-3 text-center">
                  <CheckCircle className="w-5 h-5 text-green-400 mx-auto mb-1" />
                  <p className="text-white font-bold">{checkIns.length}</p>
                  <p className="text-white/60 text-xs">Confirmed</p>
                </div>
                <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-lg p-3 text-center">
                  <XCircle className="w-5 h-5 text-red-400 mx-auto mb-1" />
                  <p className="text-white font-bold">{missedCheckIns}</p>
                  <p className="text-white/60 text-xs">Missed</p>
                </div>
                <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-lg p-3 text-center">
                  <Clock className="w-5 h-5 text-blue-400 mx-auto mb-1" />
                  <p className="text-white font-bold">{CHECK_IN_INTERVAL}m</p>
                  <p className="text-white/60 text-xs">Interval</p>
                </div>
              </div>

              {/* Instructions */}
              <div className="bg-white/5 border border-white/10 rounded-lg p-4 mb-6">
                <h3 className="text-white font-semibold text-sm mb-2">How Verification Works</h3>
                <ul className="text-white/70 text-xs space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-amber-400">1.</span>
                    <span>Keep this page open while attending your Zoom meeting</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-400">2.</span>
                    <span>Every {CHECK_IN_INTERVAL} minutes, you'll be prompted to confirm attendance</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-400">3.</span>
                    <span>Complete at least {MIN_CHECKINS_REQUIRED} check-ins for verified attendance</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-400">4.</span>
                    <span>You have {CHECKIN_GRACE_PERIOD / 60} minutes to respond to each check-in</span>
                  </li>
                </ul>
              </div>

              {/* End Session Button */}
              <Button 
                onClick={endSession}
                variant="outline"
                className="w-full border-white/30 text-white hover:bg-white/10"
                data-testid="end-session"
              >
                End Meeting Session
              </Button>

              {/* Re-open Zoom */}
              {zoomLink && (
                <a 
                  href={zoomLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-center text-white/60 text-sm mt-4 hover:text-white"
                >
                  Re-open Zoom Meeting →
                </a>
              )}
            </>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
}

export default MeetingVerificationPage;
